import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { fr } from "date-fns/locale";
import { format } from "date-fns";
import { CalendarCheck, Clock, Car, ArrowRight, Hash, Plus, ShoppingBag } from "lucide-react";
import { useVehicle } from "@/contexts/VehicleContext";
import { useCart } from "@/contexts/CartContext";
import { usePricing } from "@/contexts/PricingContext";
import { useContent } from "@/contexts/ContentContext";
import { AUTO_SUPPLEMENTS } from "@/data/textileServices";

const TIME_SLOTS = ["08:00", "09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00", "17:00"];

const CLEAN_OPTIONS = [
  { value: "exterieur", label: "Extérieur simple", priceKey: "exterieur" },
  { value: "interieur", label: "Intérieur simple", priceKey: "interieur" },
  { value: "pack", label: "Pack complet", priceKey: "pack" },
  { value: "concession", label: "Formule Concession", priceKey: "concession" },
];

export default function Booking() {
  const { category, categoryData, plate, openSelector } = useVehicle();
  const { addItem } = useCart();
  const { pricing } = usePricing();
  const { content } = useContent();
  const customSupps = content.custom?.auto_supplements || [];
  const allSupplements = [...AUTO_SUPPLEMENTS, ...customSupps];
  const customSuppPrice = (id) => {
    const c = customSupps.find((x) => x.id === id);
    return c ? (Number(c.price) || 0) : null;
  };
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [supplements, setSupplements] = useState([]);
  const [form, setForm] = useState({ service: "exterieur", notes: "" });

  const toggleSupp = (id) => {
    setSupplements((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  // Pre-fill from sessionStorage (e.g., when user clicks a clean service card)
  useEffect(() => {
    const pre = sessionStorage.getItem("ncs_preselected_service");
    if (pre && CLEAN_OPTIONS.find((s) => s.value === pre)) {
      setForm((f) => ({ ...f, service: pre }));
    }
    sessionStorage.removeItem("ncs_preselected_service");
  }, []);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Compute price based on service id (cleaning only) + supplements
  const getServicePrice = () => {
    if (!category) return null;
    const cleanOpt = CLEAN_OPTIONS.find((s) => s.value === form.service);
    return cleanOpt ? (pricing.auto[category]?.[cleanOpt.priceKey] ?? null) : null;
  };
  const basePrice = getServicePrice();
  const suppPrice = (id) => pricing.auto.supplements?.[id] ?? customSuppPrice(id) ?? 0;
  const suppTotal = supplements.reduce((sum, id) => sum + suppPrice(id), 0);
  const isDevis = category != null && basePrice === 0;
  const computedPrice = isDevis ? 0 : (basePrice !== null ? basePrice + suppTotal : null);

  const submit = (e) => {
    e.preventDefault();
    if (!category) {
      toast.error("Merci de sélectionner la catégorie de votre véhicule");
      openSelector();
      return;
    }

    const cleanOpt = CLEAN_OPTIONS.find((s) => s.value === form.service);
    const suppLabels = supplements
      .map((id) => allSupplements.find((x) => x.id === id))
      .filter(Boolean)
      .map((s) => s.label);

    const label = `${cleanOpt.label} — ${categoryData.label}${isDevis ? " (sur devis)" : ""}`;

    const metaParts = [];
    if (isDevis) metaParts.push("Sur devis — tarif confirmé par WhatsApp");
    if (plate) metaParts.push(`Plaque ${plate}`);
    if (date && time) metaParts.push(`${format(date, "EEEE d MMMM", { locale: fr })} à ${time}`);
    else if (date) metaParts.push(format(date, "EEEE d MMMM", { locale: fr }));
    if (suppLabels.length) metaParts.push(`Options : ${suppLabels.join(", ")}`);
    if (form.notes) metaParts.push(form.notes);

    addItem({
      id: `auto_${Date.now()}`,
      type: "auto",
      label,
      price: computedPrice,
      quantity: 1,
      meta: metaParts.length ? metaParts.join(" · ") : null,
    });

    toast.success("Prestation ajoutée au panier");

    // Reset the configurator (vehicle selection is kept)
    setForm({ service: "exterieur", notes: "" });
    setSupplements([]);
    setTime("");
    setDate(null);
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);

  return (
    <section id="reserver" className="relative py-28 lg:py-36 border-t border-white/5 overflow-hidden" data-testid="booking-section">
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#E50914]/10 rounded-full blur-[160px] pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-[#E50914] font-bold mb-4">
            / 06 — Réservation en ligne
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-6">
            Configurez votre<br />
            <span className="text-gradient-red">prestation auto.</span>
          </h2>
          <p className="text-white/60 text-lg leading-relaxed">
            Choisissez votre véhicule, une formule et vos options, puis ajoutez la prestation à votre panier. Date et créneau souhaités sont indicatifs — nous confirmons par WhatsApp.
          </p>
        </div>

        <VehicleSummary category={category} categoryData={categoryData} plate={plate} openSelector={openSelector} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Calendar + Slots */}
          <div className="lg:col-span-5 p-6 lg:p-8 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl">
            <div className="flex items-center gap-2 mb-5">
              <CalendarCheck className="w-5 h-5 text-[#E50914]" />
              <h3 className="font-bold text-white">1. Date souhaitée <span className="text-white/40 font-normal text-sm">(optionnel)</span></h3>
            </div>
            <div className="flex justify-center mb-8">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                locale={fr}
                disabled={(d) => d < today || d.getDay() === 0}
                className="rounded-lg border border-white/10 bg-[#050505]"
                data-testid="booking-calendar"
              />
            </div>

            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-[#E50914]" />
              <h3 className="font-bold text-white">2. Créneau souhaité</h3>
            </div>

            {!date && <p className="text-sm text-white/40 italic">Sélectionnez une date pour choisir un créneau</p>}
            {date && (
              <div className="grid grid-cols-3 gap-2">
                {TIME_SLOTS.map((t) => {
                  const isActive = time === t;
                  const cls = isActive
                    ? 'bg-[#E50914] border-[#E50914] text-white shadow-[0_0_16px_rgba(229,9,20,0.5)]'
                    : 'bg-transparent border-white/15 text-white hover:border-[#E50914]';
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTime(t)}
                      data-testid={`slot-${t}`}
                      className={`py-2.5 rounded text-sm font-semibold border transition-all ${cls}`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={submit} className="lg:col-span-7 p-6 lg:p-8 bg-gradient-to-b from-[#161616] to-[#0B0B0B] border border-white/10 rounded-xl space-y-5" data-testid="booking-form">
            <div className="flex items-center gap-2 mb-2">
              <Car className="w-5 h-5 text-[#E50914]" />
              <h3 className="font-bold text-white">3. Formule & options</h3>
            </div>

            <div>
              <label className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/60 mb-2">
                <Car className="w-4 h-4 text-[#E50914]" /> Formule de nettoyage
              </label>
              <select
                value={form.service}
                onChange={change("service")}
                data-testid="booking-service"
                className="w-full bg-[#050505] border border-white/15 rounded px-4 py-3 text-white focus:outline-none focus:border-[#E50914] transition-colors"
              >
                {CLEAN_OPTIONS.map((s) => {
                  const price = category ? (pricing.auto[category]?.[s.priceKey] ?? null) : null;
                  return (
                    <option key={s.value} value={s.value}>
                      {s.label}{category ? (price > 0 ? ` — ${price}€` : " — sur devis") : ""}
                    </option>
                  );
                })}
              </select>
              <p className="mt-2 text-xs text-white/40">
                Pour la petite mécanique, rendez-vous sur la page dédiée pour ajouter un service au panier.
              </p>
            </div>

            {/* Supplements */}
            <div>
              <label className="flex items-center gap-2 text-xs uppercase tracking-wider text-white/60 mb-3">
                <Plus className="w-4 h-4 text-[#E50914]" /> Options supplémentaires
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {allSupplements.map((s) => {
                  const active = supplements.includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleSupp(s.id)}
                      data-testid={`booking-supp-${s.id}`}
                      className={`flex items-start gap-3 p-3 rounded-lg border text-left transition-all
                        ${active ? 'bg-[#E50914]/10 border-[#E50914]/50' : 'bg-[#050505] border-white/15 hover:border-white/25'}`}
                    >
                      <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 transition-all
                        ${active ? 'bg-[#E50914] border-[#E50914]' : 'border-white/30'}`}>
                        {active && <span className="text-white text-xs font-black">✓</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className={`text-sm font-bold ${active ? 'text-white' : 'text-white/85'}`}>{s.label}</span>
                          <span className={`text-sm font-black ${active ? 'text-[#E50914]' : 'text-white/60'}`}>+{suppPrice(s.id)}€</span>
                        </div>
                        <div className="text-xs text-white/50 mt-0.5">{s.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider text-white/60 mb-2">Notes / Précisions</label>
              <textarea
                value={form.notes}
                onChange={change("notes")}
                rows="3"
                data-testid="booking-notes"
                className="w-full bg-[#050505] border border-white/15 rounded px-4 py-3 text-white resize-none focus:outline-none focus:border-[#E50914] transition-colors"
                placeholder="Modèle de véhicule exact, état, demandes particulières…"
              />
            </div>

            <Summary
              category={category}
              categoryData={categoryData}
              plate={plate}
              date={date}
              time={time}
              price={computedPrice}
              basePrice={basePrice}
              supplements={supplements}
              suppPrice={suppPrice}
              allSupplements={allSupplements}
            />

            <button
              type="submit"
              data-testid="booking-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-bold uppercase text-sm tracking-wider px-6 py-4 rounded-lg border border-[#E50914] hover:shadow-[0_0_32px_rgba(229,9,20,0.5)] transition-all"
            >
              <ShoppingBag className="w-4 h-4" /> Ajouter au panier
            </button>

            <p className="text-xs text-white/40 text-center">
              Ajoutez plusieurs prestations puis envoyez votre demande en une fois via WhatsApp.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function VehicleSummary({ category, categoryData, plate, openSelector }) {
  if (!category) {
    return (
      <div className="mb-8 p-5 sm:p-6 rounded-xl border border-[#E50914]/40 bg-[#E50914]/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="booking-vehicle-empty">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-[#E50914]/20 border border-[#E50914]/40 flex items-center justify-center shrink-0">
            <Car className="w-5 h-5 text-[#E50914]" />
          </div>
          <div>
            <div className="font-bold text-white">Sélectionnez votre véhicule</div>
            <div className="text-xs text-white/60">Obligatoire pour réserver — tarifs précis selon la catégorie.</div>
          </div>
        </div>
        <button
          onClick={openSelector}
          data-testid="booking-select-vehicle-btn"
          className="inline-flex items-center gap-2 bg-[#E50914] hover:bg-[#F87171] active:scale-[0.98] text-white font-bold uppercase text-xs tracking-wider px-5 py-2.5 rounded-lg border border-[#E50914] transition-all whitespace-nowrap"
        >
          Choisir mon véhicule <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }
  return (
    <div className="mb-8 p-4 sm:p-5 rounded-xl border border-white/10 bg-[#0A0A0A] flex items-center justify-between gap-4" data-testid="booking-vehicle-selected">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#E50914] flex items-center justify-center font-black text-white">
          {category}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Véhicule</div>
          <div className="font-bold text-white text-sm leading-tight">{categoryData.name}</div>
          {plate && (
            <div className="text-xs text-white/50 flex items-center gap-1 mt-0.5">
              <Hash className="w-3 h-3" /> <span className="font-mono font-bold tracking-wider">{plate}</span>
            </div>
          )}
        </div>
      </div>
      <button
        onClick={openSelector}
        data-testid="booking-change-vehicle-btn"
        className="text-xs uppercase tracking-wider text-white/60 hover:text-[#E50914] font-semibold transition-colors"
      >
        Modifier
      </button>
    </div>
  );
}

function Summary({ category, categoryData, plate, date, time, price, basePrice, supplements, suppPrice, allSupplements }) {
  if (!date && !time && !category) return null;
  const supps = (supplements || []).map((id) => (allSupplements || AUTO_SUPPLEMENTS).find((x) => x.id === id)).filter(Boolean);
  return (
    <div className="p-4 bg-[#E50914]/10 border border-[#E50914]/30 rounded space-y-2 text-sm">
      {category && (
        <div className="flex items-center gap-2 text-white">
          <Car className="w-4 h-4 text-[#E50914]" />
          <span className="text-white/60">Catégorie :</span>
          <span className="font-semibold">{categoryData.label} — {categoryData.name}</span>
          {plate && <span className="text-white/40 text-xs font-mono ml-2">[{plate}]</span>}
        </div>
      )}
      {date && time && (
        <div className="flex items-center gap-2 text-white">
          <CalendarCheck className="w-4 h-4 text-[#E50914]" />
          <span className="text-white/60">Créneau :</span>
          <span className="font-semibold">{format(date, "EEEE d MMMM", { locale: fr })} à {time}</span>
        </div>
      )}
      {basePrice !== null && basePrice > 0 && supps.length > 0 && (
        <div className="pt-2 border-t border-white/10 space-y-1 text-xs text-white/70">
          <div className="flex justify-between"><span>Formule</span><span>{basePrice}€</span></div>
          {supps.map((s) => (
            <div key={s.id} className="flex justify-between">
              <span>+ {s.label}</span><span>+{suppPrice ? suppPrice(s.id) : s.price}€</span>
            </div>
          ))}
        </div>
      )}
      {price !== null && (
        <div className="flex items-center justify-between text-white pt-2 border-t border-white/10">
          <span className="text-white/60">Total estimé :</span>
          <span className="font-black text-2xl text-[#E50914]">{price > 0 ? `${price}€` : "Sur devis"}</span>
        </div>
      )}
    </div>
  );
}
